import { isMocked } from '../utils/isMocked';
import { MOCK_MEMORY_STORAGE } from 'e2e-tests/mocks/mock-rooms';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { MovesEnum } from 'shared/constants/shared-enums';
import { TxStatus } from 'shared/constants/types';

import { safeBrowserStorage } from './safe-browser-storage.util';

type HashDateObject = {
  hash: `0x${string}`;
  date: Date;
};

export interface State {
  playMoves: Record<string, PlayMove>;
  allowanceTransactions: {
    amount: number | null;
    hash: `0x${string}` | null;
    date: Date | null;
  };
  mainSalt: Record<string, string | null>;
  startHash: Record<string, HashDateObject | null>;
  joinHash: Record<string, HashDateObject | null>;
  noOneJoinHash: Record<string, HashDateObject | null>;
  closeHash: Record<string, HashDateObject | null>;
}
interface PlayMove {
  salt: string | null;
  move: MovesEnum;
  playerAddress: string;
  moveHash: HashDateObject | null;
  revealHash: HashDateObject | null;
  moveStatus: TxStatus | null;
  revealStatus: TxStatus | null;
}

export interface Actions {
  setPlayMove: (key: string, arg0: PlayMove) => void;
  deletePlayMove: (key: string) => void;
  setRevealHash: (key: string, hash: `0x${string}` | null, date?: Date, status?: TxStatus) => void;
  setAllowanceHash: (hash: `0x${string}` | null, date?: Date) => void;
  setAllowanceAmount: (amount: number) => void;
  setStartHash: (chainId: number, hash: `0x${string}` | null, date?: Date) => void;
  setJoinHash: (chainId: number, hash: `0x${string}` | null, date?: Date) => void;
  setNoOneJoinHash: (chainId: number, hash: `0x${string}` | null, date?: Date) => void;
  setCloseHash: (chainId: number, hash: `0x${string}` | null, date?: Date) => void;
  setMainSalt: (key: string, salt: string) => void;
}

export const initialState: State = {
  playMoves: {},
  allowanceTransactions: {
    amount: null,
    hash: null,
    date: null,
  },
  startHash: {},
  joinHash: {},
  noOneJoinHash: {},
  closeHash: {},
  mainSalt: {},
};

export const usePlayerGamesStore = create<State & Actions>()(
  persist(
    immer((set) => ({
      ...initialState,
      setPlayMove: (key, playMove) =>
        set((state) => ({
          playMoves: {
            ...state.playMoves,
            [key]: playMove,
          },
        })),
      deletePlayMove: (key) =>
        set((state) => {
          Object.keys(state.playMoves).forEach((playMoveKey) => {
            if (playMoveKey.startsWith(key)) {
              delete state.playMoves[playMoveKey];
            }
          });
        }),

      setRevealHash: (key, hash, date, status) =>
        set((state) => {
          if (state.playMoves[key]) {
            if (hash) {
              state.playMoves[key].revealHash = {
                hash,
                date: date || new Date(),
              };
            } else {
              state.playMoves[key].revealHash = null;
            }
            state.playMoves[key].revealStatus = status || null;
          }
        }),
      setAllowanceHash: (hash, date) =>
        set((state) => {
          if (hash) state.allowanceTransactions.hash = hash;
        }),
      setAllowanceAmount: (amount) =>
        set((state) => {
          state.allowanceTransactions.amount = amount;
        }),
      setStartHash: (chainId, hash, date) =>
        set((state) => {
          if (hash) {
            state.startHash[chainId] = {
              hash,
              date: date || new Date(),
            };
          } else {
            state.startHash[chainId] = null;
          }
        }),
      setJoinHash: (chainId, hash, date) =>
        set((state) => {
          if (hash) {
            state.joinHash[chainId] = {
              hash,
              date: date || new Date(),
            };
          } else {
            state.joinHash[chainId] = null;
          }
        }),
      setNoOneJoinHash: (chainId, hash, date) =>
        set((state) => {
          if (hash) {
            state.noOneJoinHash[chainId] = {
              hash,
              date: date || new Date(),
            };
          } else {
            state.noOneJoinHash[chainId] = null;
          }
        }),
      setCloseHash: (chainId, hash, date) =>
        set((state) => {
          if (hash) {
            state.closeHash[chainId] = {
              hash,
              date: date || new Date(),
            };
          } else {
            state.closeHash[chainId] = null;
          }
        }),
      setMainSalt: (key, salt) =>
        set((state) => {
          state.mainSalt = {
            ...state.mainSalt,
            [key]: salt,
          };
        }),
    })),
    {
      name: 'playerGamesStore',
      storage: isMocked ? MOCK_MEMORY_STORAGE : createJSONStorage(safeBrowserStorage),
    },
  ),
);
